import { Spin } from "antd";
import { useCallback, useMemo } from "react";
import { MapContainer, Marker, Polygon, Popup, TileLayer } from "react-leaflet";
import { Link, useNavigate } from "react-router-dom";
import { useBrigadeRatings } from "@/modules/dashboard/hooks/useDashboard";
import { useEquipmentLive } from "@/modules/equipment/hooks/useEquipment";
import type { LiveEquipmentPosition } from "@/modules/equipment/types";
import { usePlantingsGeoJSON } from "@/modules/plantings/hooks/usePlantings";
import { plantTypeColor, plantTypeLabel } from "@/modules/plantings/utils/format";
import { useStaffLocationLive } from "@/modules/staff-location/hooks/useStaffLocation";
import { useTerritories } from "@/modules/territories/hooks/useTerritories";
import type { Territory } from "@/modules/territories/services/territory.service";
import { FitBounds } from "@/shared/map/FitBounds";
import {
	geoJsonToLeafletPositions,
	geometryToLatLngs,
	polygonToLatLng,
	toLatLng,
} from "@/shared/map/geojson";
import {
	createAttendanceIcon,
	createLiveCarIcon,
	createPlantIcon,
	createStaffIcon,
	EQUIPMENT_STATUS_LABELS,
	equipmentStatusColor,
} from "@/shared/map/icons";
import {
	BRIGADE_RATING_COLORS,
	brigadeOverdueOpacity,
	brigadeRatingStatus,
} from "@/shared/utils/brigadeRating";
import { useAttendanceMapLive } from "../hooks/useAttendanceMap";
import type { MonitoringLayer } from "../types";

/** Org-level (no assigned brigade) territory color — task-count driven, see territoryFillOpacity. */
const ORG_TERRITORY_COLOR = "#1677ff";
/** Default fill opacity for brigade territories that aren't in the RED (overdue) state. */
const BRIGADE_TERRITORY_BASE_OPACITY = 0.35;

/** More active tasks in a territory -> denser fill, so busy territories stand out at a glance. */
const territoryFillOpacity = (taskCount: number): number => Math.min(0.7, 0.08 + taskCount * 0.08);

const DEFAULT_CENTER: [number, number] = [41.56, 60.62];
const DEFAULT_ZOOM = 7;

type LatLng = [number, number];

const territoryPositions = (
	items: Array<Territory & { coordinates: NonNullable<Territory["coordinates"]> }>
): LatLng[] => items.flatMap((t) => geometryToLatLngs(t.coordinates));

const equipmentPositions = (
	items: Array<LiveEquipmentPosition & { position: NonNullable<LiveEquipmentPosition["position"]> }>
): LatLng[] => items.map((e) => [e.position.latitude, e.position.longitude]);

const flatPositions = (items: Array<{ latitude: number; longitude: number }>): LatLng[] =>
	items.map((i) => [i.latitude, i.longitude]);

interface MonitoringMapProps {
	organizationId: string;
	activeLayers: MonitoringLayer[];
	/** Case-insensitive name filter applied across every active layer (staff, equipment, territory, planting). */
	search?: string;
}

export function MonitoringMap({ organizationId, activeLayers, search }: MonitoringMapProps) {
	const navigate = useNavigate();

	const has = (layer: MonitoringLayer) => activeLayers.includes(layer);
	const query = search?.trim().toLocaleLowerCase("uz");
	const matches = useCallback(
		(...names: (string | null | undefined)[]) =>
			!query || names.some((name) => name?.toLocaleLowerCase("uz").includes(query)),
		[query]
	);

	const showTerritory = has("territory-org") || has("territory-brigade");
	const { data: territoriesData, isFetching: isFetchingTerritories } = useTerritories(
		// Brigade assignments always live on nested (non-top-level) territories, so
		// that layer needs every depth; the org layer keeps the default top-level-only
		// fetch to avoid rendering a parent boundary stacked with its unassigned children.
		{ organizationId, page: 1, limit: 100, flat: has("territory-brigade") },
		{ enabled: showTerritory }
	);
	const { data: brigadeRatingsData } = useBrigadeRatings(
		has("territory-brigade") ? organizationId : ""
	);
	const { data: equipmentData, isFetching: isFetchingEquipment } = useEquipmentLive(
		{ organizationId },
		{ enabled: has("equipment") }
	);
	const { data: staffData, isFetching: isFetchingStaff } = useStaffLocationLive(
		{ organizationId },
		{ enabled: has("staff") }
	);
	const { data: plantingsData, isFetching: isFetchingPlantings } = usePlantingsGeoJSON(
		{ organizationId },
		{ enabled: has("planting") }
	);
	const { data: attendanceData, isFetching: isFetchingAttendance } = useAttendanceMapLive(
		{ organizationId },
		{ enabled: has("attendance") }
	);

	const territories = useMemo(
		() =>
			(territoriesData?.data?.items ?? []).filter(
				(t): t is Territory & { coordinates: NonNullable<Territory["coordinates"]> } =>
					!!t.coordinates?.coordinates?.length && matches(t.name)
			),
		[territoriesData, matches]
	);
	const orgTerritories = useMemo(
		() => territories.filter((t) => t.assignedBrigades.length === 0),
		[territories]
	);
	const brigadeTerritories = useMemo(
		() => territories.filter((t) => t.assignedBrigades.length > 0),
		[territories]
	);
	const brigadeRatingsById = useMemo(
		() => new Map((brigadeRatingsData?.brigades ?? []).map((b) => [b.brigadeId, b])),
		[brigadeRatingsData]
	);
	const equipment = useMemo(
		() =>
			(equipmentData ?? []).filter(
				(
					e
				): e is LiveEquipmentPosition & {
					position: NonNullable<LiveEquipmentPosition["position"]>;
				} =>
					e.position !== null && matches(e.equipmentName, e.licensePlate, e.assignedStaff?.fullname)
			),
		[equipmentData, matches]
	);
	const staff = useMemo(
		() => (staffData ?? []).filter((s) => matches(s.fullname, s.brigadeName)),
		[staffData, matches]
	);
	const features = useMemo(
		() => (plantingsData?.features ?? []).filter((f) => matches(f.properties.plantName)),
		[plantingsData, matches]
	);
	const attendance = useMemo(
		() => (attendanceData ?? []).filter((a) => matches(a.fullname, a.brigadeName)),
		[attendanceData, matches]
	);

	// Aggregate positions for a single fit-to-bounds per org (FitBounds gates on key).
	// NB: append with a loop, never `acc.push(...arr)` — spreading a large array
	// (e.g. a region polygon with tens of thousands of vertices) blows the call
	// stack ("Maximum call stack size exceeded").
	const allPositions = useMemo(() => {
		const acc: LatLng[] = [];
		const add = (positions: LatLng[]) => {
			for (const p of positions) {
				acc.push(p);
			}
		};
		if (activeLayers.includes("territory-org")) {
			add(territoryPositions(orgTerritories));
		}
		if (activeLayers.includes("territory-brigade")) {
			add(territoryPositions(brigadeTerritories));
		}
		if (activeLayers.includes("equipment")) {
			add(equipmentPositions(equipment));
		}
		if (activeLayers.includes("staff")) {
			add(flatPositions(staff));
		}
		if (activeLayers.includes("planting")) {
			add(features.flatMap((f) => geometryToLatLngs(f.geometry)));
		}
		if (activeLayers.includes("attendance")) {
			add(flatPositions(attendance));
		}
		return acc;
	}, [orgTerritories, brigadeTerritories, equipment, staff, features, attendance, activeLayers]);

	const isFetching =
		isFetchingTerritories ||
		isFetchingEquipment ||
		isFetchingStaff ||
		isFetchingPlantings ||
		isFetchingAttendance;

	return (
		<div className="relative h-[calc(100vh-260px)] min-h-[420px] w-full overflow-hidden rounded-xl border border-gray-200 shadow-sm">
			{isFetching && (
				<div className="absolute right-3 top-3 z-[1000] rounded-md bg-white/90 px-2 py-1 shadow">
					<Spin size="small" />
				</div>
			)}
			<MapContainer
				center={DEFAULT_CENTER}
				zoom={DEFAULT_ZOOM}
				className="h-full w-full"
				scrollWheelZoom
				preferCanvas
			>
				<TileLayer
					attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
				/>
				{allPositions.length > 0 && <FitBounds positions={allPositions} fitKey={organizationId} />}

				{/* Org-level territory polygons (no assigned brigade — city/org boundaries) */}
				{has("territory-org") &&
					orgTerritories.map((territory) => (
						<Polygon
							key={territory.id}
							positions={geoJsonToLeafletPositions(territory.coordinates.coordinates) as any}
							pathOptions={{
								color: ORG_TERRITORY_COLOR,
								fillColor: ORG_TERRITORY_COLOR,
								fillOpacity: territoryFillOpacity(territory.taskCount),
								weight: 2,
							}}
						>
							<Popup>
								<div className="text-sm min-w-[180px]">
									<p className="font-semibold text-base mb-1">{territory.name}</p>
									<p className="text-gray-500 text-xs mb-1">{territory.organizationName}</p>
									<p className="mb-1">
										Yuzasi: <strong>{territory.area?.toLocaleString("uz-UZ") ?? "—"} m²</strong>
									</p>
									<p className="mb-1">
										Xodimlar: <strong>{territory.assignedStaffCount ?? 0}</strong>
									</p>
									<p className="mb-2">
										Faol topshiriqlar: <strong>{territory.taskCount}</strong>
									</p>
									<Link to={`/territories/${territory.id}`} className="text-blue-600 text-xs">
										Batafsil →
									</Link>
								</div>
							</Popup>
						</Polygon>
					))}

				{/* Brigade-level territory polygons — colored by the assigned brigade's task-completion rating */}
				{has("territory-brigade") &&
					brigadeTerritories.map((territory) => {
						const brigadeRef = territory.assignedBrigades[0];
						const rating = brigadeRef ? brigadeRatingsById.get(brigadeRef.id) : undefined;
						const status = rating ? brigadeRatingStatus(rating) : "GREEN";
						const color = BRIGADE_RATING_COLORS[status];
						const fillOpacity =
							status === "RED"
								? brigadeOverdueOpacity(rating?.overdueIncompleteCount ?? 0)
								: BRIGADE_TERRITORY_BASE_OPACITY;
						return (
							<Polygon
								key={territory.id}
								positions={geoJsonToLeafletPositions(territory.coordinates.coordinates) as any}
								pathOptions={{ color, fillColor: color, fillOpacity, weight: 2 }}
							>
								<Popup>
									<div className="text-sm min-w-[180px]">
										<p className="font-semibold text-base mb-1">{territory.name}</p>
										<p className="text-gray-500 text-xs mb-1">
											{brigadeRef?.name ?? territory.organizationName}
										</p>
										{rating && (
											<>
												<p className="mb-1">
													Bajarilgan: <strong>{Math.round(rating.completionRate)}%</strong>
												</p>
												{rating.overdueIncompleteCount > 0 && (
													<p className="mb-1 text-red-600">
														Muddati o'tgan: <strong>{rating.overdueIncompleteCount}</strong>
													</p>
												)}
											</>
										)}
										<p className="mb-2">
											Faol topshiriqlar: <strong>{territory.taskCount}</strong>
										</p>
										<Link to={`/territories/${territory.id}`} className="text-blue-600 text-xs">
											Batafsil →
										</Link>
									</div>
								</Popup>
							</Polygon>
						);
					})}

				{/* Equipment markers (rotated by heading) */}
				{has("equipment") &&
					equipment.map((eq) => (
						<Marker
							key={eq.equipmentId}
							position={[eq.position.latitude, eq.position.longitude]}
							icon={createLiveCarIcon(eq.status, eq.position.heading, eq.insideTerritory)}
						>
							<Popup>
								<div className="text-sm min-w-[170px]">
									<p className="font-semibold mb-1">{eq.equipmentName}</p>
									{eq.type && <p className="text-gray-500 text-xs mb-1">{eq.type.name}</p>}
									{eq.licensePlate && (
										<p className="mb-1">
											Raqam: <strong>{eq.licensePlate}</strong>
										</p>
									)}
									<p className="mb-1">
										Holat:{" "}
										<span
											style={{
												color: equipmentStatusColor(eq.status, eq.insideTerritory),
												fontWeight: 600,
											}}
										>
											{EQUIPMENT_STATUS_LABELS[eq.status] ?? eq.status}
											{eq.status === "online" &&
												eq.insideTerritory === false &&
												" — hududdan tashqarida"}
										</span>
									</p>
									{eq.assignedStaff && (
										<p className="mb-2">
											Haydovchi: <strong>{eq.assignedStaff.fullname}</strong>
										</p>
									)}
									<button
										type="button"
										className="text-blue-600 text-xs cursor-pointer bg-none border-none p-0 underline"
										onClick={() => navigate(`/equipment/${eq.equipmentId}`)}
									>
										GPS tarixini ko'rish →
									</button>
								</div>
							</Popup>
						</Marker>
					))}

				{/* Staff / brigadier markers */}
				{has("staff") &&
					staff.map((s) => (
						<Marker
							key={s.staffId}
							position={[s.latitude, s.longitude]}
							icon={createStaffIcon(s.isOnline)}
						>
							<Popup>
								<div className="text-sm min-w-[190px]">
									<p className="font-semibold mb-1">{s.fullname}</p>
									{s.organizationName && (
										<p className="text-gray-500 text-xs mb-1">Tashkilot: {s.organizationName}</p>
									)}
									{s.brigadeName && (
										<p className="text-gray-500 text-xs mb-1">Brigada: {s.brigadeName}</p>
									)}
									{s.territories.length > 0 && (
										<p className="text-gray-500 text-xs mb-1">
											Hududlar: {s.territories.map((t) => t.name).join(", ")}
										</p>
									)}
									<p className="mb-1">
										Holat:{" "}
										<span style={{ color: s.isOnline ? "#1677ff" : "#94a3b8", fontWeight: 600 }}>
											{s.isOnline ? "Onlayn" : "Oflayn"}
										</span>
									</p>
									<p className="text-gray-400 text-xs mb-2">
										{new Date(s.recordedAt).toLocaleString("uz-UZ")}
									</p>
									<Link
										to={`/staff-location/${s.staffId}/history`}
										className="text-blue-600 text-xs"
									>
										Marshrut →
									</Link>
								</div>
							</Popup>
						</Marker>
					))}

				{/* Planting GeoJSON (Point / MultiPoint / Polygon / MultiPolygon) */}
				{has("planting") &&
					features.map((feature) => {
						const { id, plantType, plantName, quantity, plantedAt, territoryId } =
							feature.properties;
						const color = plantTypeColor(plantType);
						const g = feature.geometry;
						const popup = (
							<Popup>
								<div className="text-sm min-w-[170px]">
									<p className="font-semibold mb-1">{plantName ?? "O'simlik"}</p>
									<p className="text-gray-500 text-xs mb-1">{plantTypeLabel(plantType)}</p>
									<p className="mb-1">
										Soni: <strong>{quantity ?? "—"}</strong>
									</p>
									<p className="mb-1">
										Ekilgan: <strong>{plantedAt?.slice(0, 10) ?? "—"}</strong>
									</p>
									{territoryId && (
										<Link
											to={`/territories?territoryId=${territoryId}`}
											className="text-blue-600 text-xs"
										>
											Hududni ko'rish →
										</Link>
									)}
								</div>
							</Popup>
						);

						if (g.type === "Point") {
							return (
								<Marker key={id} position={toLatLng(g.coordinates)} icon={createPlantIcon(color)}>
									{popup}
								</Marker>
							);
						}
						if (g.type === "MultiPoint") {
							return g.coordinates.map((coord) => (
								<Marker
									key={`${id}-${coord[0]}-${coord[1]}`}
									position={toLatLng(coord)}
									icon={createPlantIcon(color)}
								>
									{popup}
								</Marker>
							));
						}
						if (g.type === "Polygon") {
							return (
								<Polygon
									key={id}
									positions={polygonToLatLng(g.coordinates)}
									pathOptions={{ color, fillColor: color, fillOpacity: 0.35, weight: 2 }}
								>
									{popup}
								</Polygon>
							);
						}
						return g.coordinates.map((poly) => (
							<Polygon
								key={`${id}-${poly[0]?.[0]?.[0]}-${poly[0]?.[0]?.[1]}`}
								positions={polygonToLatLng(poly)}
								pathOptions={{ color, fillColor: color, fillOpacity: 0.35, weight: 2 }}
							>
								{popup}
							</Polygon>
						));
					})}

				{/* Attendance event markers */}
				{has("attendance") &&
					attendance.map((a) => (
						<Marker
							key={a.staffId}
							position={[a.latitude, a.longitude]}
							icon={createAttendanceIcon(a.insideTerritory)}
						>
							<Popup>
								<div className="text-sm min-w-[170px]">
									<p className="font-semibold mb-1">{a.fullname}</p>
									{a.brigadeName && (
										<p className="text-gray-500 text-xs mb-1">Brigada: {a.brigadeName}</p>
									)}
									<p className="mb-1">
										Hududda:{" "}
										<strong>
											{a.insideTerritory === null ? "—" : a.insideTerritory ? "Ha" : "Yo'q"}
										</strong>
									</p>
									<p className="text-gray-400 text-xs mb-2">
										{new Date(a.eventTime).toLocaleString("uz-UZ")}
									</p>
									<Link to="/attendance/daily" className="text-blue-600 text-xs">
										Batafsil →
									</Link>
								</div>
							</Popup>
						</Marker>
					))}
			</MapContainer>
		</div>
	);
}
